import { FormHelperText, TextField } from "@mui/material"
import { trackPopUpClicks } from "common/analytics"
import { getDay, setHours, setMinutes } from "date-fns"
import he from "date-fns/locale/he"
import { useField } from "formik"
// import { TextField } from "formik-mui"
import { useEffect, useRef } from "react"
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import styled from "styled-components"
// https://reactdatepicker.com/

const StyledWrapper = styled.div`
  height: 81.6px;
  display: flex;
  width: 100%;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
`
const StyledError = styled(FormHelperText)`
  bottom: -0.5em;
  right: 0px;
  font-size: 12px !important;
  height: 15.594px;
  margin-top: 3px;
`

const StyledPickerWrapper = styled.div<{ $default: boolean }>`
  height: fit-content;
  width: 100%;
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }
  .react-datepicker-popper {
    z-index: 10;
  }
  * {
    font-family: MazdaType, OENaPro, sans-serif;
  }
  .react-datepicker__time-list-item--selected {
    background-color: ${(p) => p.theme.colors.mazdaRed} !important;
  }
  .react-datepicker__day--selected {
    background-color: ${(p) => p.theme.colors.mazdaRed} !important;
  }
  .react-datepicker__header {
    background-color: ${(p) => p.theme.colors.lightGrey} !important;
  }
`

type TimeSlot = { min: string; max: string }
type CalendarInputProps = {
  name: string
  label: string
  allowedTimeSlots?: TimeSlot[]
  minTime?: number
  maxTime?: number
  disable?: boolean
  isWorkingFriday?: boolean
  availableDays?: number[] // Array of JS day numbers (0=Sunday, 1=Monday, etc.)
}

const DateTimeInput = ({
  name,
  label,
  allowedTimeSlots,
  minTime,
  maxTime,
  disable,
  isWorkingFriday,
  availableDays,
}: CalendarInputProps) => {
  // formik
  const [field, meta, helpers] = useField(name)
  const { value, error, touched } = meta
  const { setValue } = helpers

  // No local date state: use Formik's value as the source of truth
  const ref = useRef(null)

  // Filter dates based on available days or default logic
  const isWeekday = (date) => {
    const day = getDay(date)

    // If availableDays is provided, use it to filter dates
    if (availableDays && availableDays.length > 0) {
      return availableDays.includes(day)
    }

    // Default logic: exclude Friday/Saturday or just Saturday
    if (isWorkingFriday) {
      return day !== 6
    }
    return day !== 5 && day !== 6
  }
  // Filter times: only allow times within any allowed slot
  const filterPassedTime = (time) => {
    // If no allowed time slots are defined, don't allow any time
    if (!allowedTimeSlots || allowedTimeSlots.length === 0) return false

    const selectedDate = new Date(time)
    const selectedHour = selectedDate.getHours()
    const selectedMinute = selectedDate.getMinutes()

    // Check if selected time is within any slot
    return allowedTimeSlots.some((slot) => {
      const [minHour, minMinute] = slot.min.split(":").map(Number)
      const [maxHour, maxMinute] = slot.max.split(":").map(Number)
      const afterMin =
        selectedHour > minHour ||
        (selectedHour === minHour && selectedMinute >= minMinute)
      const beforeMax =
        selectedHour < maxHour ||
        (selectedHour === maxHour && selectedMinute <= maxMinute)
      return afterMin && beforeMax
    })
  }

  const getDateTwoDaysFromNow = (): Date => {
    const now = new Date()
    const twoDaysFromNow = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 2
    )
    return twoDaysFromNow
  }

  const minDate = getDateTwoDaysFromNow()

  // Removed effect syncing date and value to prevent infinite loop

  // Reset date when time constraints change
  useEffect(() => {
    if (value !== null && value !== undefined) {
      const selectedDate = new Date(value)
      const selectedHours = selectedDate.getHours()
      const isTimeInvalid =
        (typeof minTime === "number" && selectedHours < minTime) ||
        (typeof maxTime === "number" && selectedHours > maxTime)
      if (isTimeInvalid && value !== null) {
        setValue(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minTime, maxTime, value])

  // Reset time when slots change and time becomes invalid, but preserve the date
  useEffect(() => {
    if (value !== null && value !== undefined && allowedTimeSlots) {
      const timeoutId = setTimeout(() => {
        // Check if current time is valid for the new slots
        const isTimeValid = filterPassedTime(value)
        if (!isTimeValid) {
          // Preserve the date, reset only time to earliest available time
          const dateOnly = new Date(value)

          // Find the first available time slot and set to its minimum time
          if (allowedTimeSlots.length > 0) {
            const firstSlot = allowedTimeSlots[0]
            const [minHour, minMinute] = firstSlot.min.split(":").map(Number)
            dateOnly.setHours(minHour, minMinute, 0, 0)
          } else {
            // If no slots available, set to start of day
            dateOnly.setHours(0, 0, 0, 0)
          }

          setValue(dateOnly)
        }
      }, 0) // Use timeout to break potential loops

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedTimeSlots])

  setDefaultLocale("he_IL")
  registerLocale("he_IL", he)

  return (
    <StyledWrapper>
      <StyledPickerWrapper $default={!!value}>
        <DatePicker
          customInput={<TextField label={label} {...{ ...field, value: "" }} />}
          required
          ref={ref}
          locale="he_IL"
          className="form-control"
          selected={value instanceof Date ? value : null}
          onChange={(date) => {
            setValue(date)
          }}
          name={name}
          onCalendarOpen={() => {
            trackPopUpClicks("קביעת ביקור", "treatmentDate")
          }}
          // date:
          dateFormat="dd/MM/yy, HH:mm"
          // maxDate={twoWeeksFromNow}
          minDate={minDate}
          filterDate={isWeekday}
          //time:
          showTimeSelect
          timeIntervals={30}
          timeFormat="HH:mm"
          timeCaption="שעה"
          minTime={
            allowedTimeSlots && allowedTimeSlots.length > 0
              ? setHours(
                  setMinutes(new Date(), 0),
                  Number(allowedTimeSlots[0].min.split(":")[0])
                )
              : minTime !== undefined
              ? setHours(setMinutes(new Date(), 0), minTime)
              : undefined
          }
          maxTime={
            allowedTimeSlots && allowedTimeSlots.length > 0
              ? setHours(
                  setMinutes(new Date(), 0),
                  Number(
                    allowedTimeSlots[allowedTimeSlots.length - 1].max.split(
                      ":"
                    )[0]
                  )
                )
              : maxTime !== undefined
              ? setHours(setMinutes(new Date(), 0), maxTime)
              : undefined
          }
          filterTime={filterPassedTime}
          autoComplete="off"
          disabled={
            // Disable only if explicitly asked OR there are truly no selectable days
            disable || (!!availableDays && availableDays.length === 0)
          }

          // maxTime={isToday && maxTimeState}
          // onCalendarOpen={handleCalendarOpen}
          // onCalendarClose={handleCalendarClose}
          //   open={isOpen}
          //   onSelect={checkCurrentDay}
        />
      </StyledPickerWrapper>
      {error && touched ? (
        <StyledError id={`${name}-helper-text`} error>
          {error}
        </StyledError>
      ) : !disable && availableDays && availableDays.length === 0 ? (
        <StyledError id={`${name}-helper-text`}>
          אין מועדים פנויים להזמנה
        </StyledError>
      ) : (
        <StyledError id={`${name}-helper-text`}>{"  "}</StyledError>
      )}
    </StyledWrapper>
  )
}
export default DateTimeInput
